// =============================================================================
// App.tsx — CRYB bare-RN root (P1 SHELL).
//
// Implements the blueprint §2 6-navigator tree as PLACEHOLDER screens so the
// shell compiles + renders + navigates on-device before any real screen is
// ported (P3). Provider tree, gate, linking, navigationRef and modal
// presentations are wired here; P2 thickens this (real useAuth, FloatingTabBar,
// InAppNotification, video splash).
//
// NOTE: there is intentionally NO wallet/wagmi provider in the tree for v1 —
// crypto is gated OFF (SHOW_CRYPTO=false) and the WalletConnect native module
// breaks the New-Architecture iOS build. The crypto screens are gated
// placeholders that don't touch wagmi, so the boot path stays wallet-free.
// Re-introduce a wallet provider here when crypto un-gates (CRYB_RN_MIGRATION.md).
// =============================================================================
import React from 'react';
import { StatusBar } from 'react-native';
import { LayoutAnimationConfig } from 'react-native-reanimated';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { NavigationContainer, DefaultTheme } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';

import type { RootStackParamList } from './navigation/types';
import { navigationRef } from './navigation/navigationRef';
import { linking } from './navigation/linking';
import { useAuth } from './navigation/useAuth';
import { AuthStack } from './navigation/AuthStack';
import { Tabs } from './navigation/Tabs';
import { SettingsStack } from './navigation/SettingsStack';
import { CryptoStack } from './navigation/CryptoStack';
import { makePlaceholder } from './screens/Placeholder';
import { makeComingSoon } from './components/ComingSoonScreen';
import { makeUnavailable } from './components/ScreenUnavailable';
import ErrorBoundary from './components/ErrorBoundary';
import SearchScreen from './screens/SearchScreen';
import StoryCreateScreen from './screens/StoryCreate';
import StoryViewerScreen from './screens/StoryViewer';
// P3-6 PROFILE group ported screens.
import UserProfileScreen from './screens/UserProfileScreen';
import EditProfileScreen from './screens/EditProfileScreen';
import LeaderApplyScreen from './screens/LeaderApplyScreen';
import LeaderDashboardScreen from './screens/LeaderDashboardScreen';
import LeaderOnboardingScreen from './screens/LeaderOnboardingScreen';
import CommunityManageScreen from './screens/CommunityManageScreen';
import FollowListScreen from './screens/FollowListScreen';
// P3-2 FEED/HOME group ported screens.
import PostDetailScreen from './app/post/[id]';
import CreatePostScreen from './app/create-post';
import BookmarksScreen from './app/bookmarks';
// P3-3 COMMUNITIES/CHANNELS group ported screens.
import CommunitiesScreen from './screens/CommunitiesScreen';
import CommunityScreen from './screens/CommunityScreen';
import ServerDetailScreen from './screens/ServerDetailScreen';
import ChannelScreen from './screens/ChannelScreen';
import LegalScreen from './screens/LegalScreen';
// P3 wave-2 MISC SOCIAL group ported screens.
import CreateCommunityScreen from './screens/CreateCommunityScreen';
import LearnScreen from './screens/LearnScreen';
import LearnArticleScreen from './screens/LearnArticleScreen';
import DiscoverServersScreen from './screens/DiscoverServersScreen';
// P3-8 NOTIFICATIONS group ported screen + in-app banner host.
import NotificationsScreen from './screens/NotificationsScreen';
import InAppNotification from './components/notifications/InAppNotification';
// P3 DIRECT MESSAGES group ported screens (DM chat + New Message compose).
import DMScreen from './screens/DMScreen';
import DMNewScreen from './screens/DMNewScreen';
// P3-5 CALLING group ported screens (LiveKit) + root incoming-call ring handler.
// LAUNCH-CRASH FIX: VoiceCallScreen + VoiceRoomScreen import @livekit, whose
// WebRTC native module initializes at module-eval and THROWS on bridgeless New
// Arch if unresolved → crashes launch before any UI. They are lazy-loaded via
// `getComponent` in the navigator below so @livekit stays OFF the boot graph and
// only loads when a call is actually opened. VoiceRoomsScreen is @livekit-free.
// Markets group (info-only for v1; transactional surfaces are v2-gated).
import MarketsScreen from './screens/MarketsScreen';
import CoinDetailScreen from './screens/CoinDetailScreen';
import PortfolioScreen from './screens/PortfolioScreen';
import VoiceRoomsScreen from './screens/VoiceRoomsScreen';
import IncomingCallHandler from './components/IncomingCallHandler';
// P2-DRAWER: X-style account drawer, opened by the top-left avatar via the
// exported openDrawer() controller. Mounted above the navigator (uses
// navigationRef, not a screen hook) so it overlays the tab navigator from
// anywhere. Renders null until opened.
import DrawerMenu from './components/nav/DrawerMenu';
import CallInProgressBanner from './components/call/CallInProgressBanner';
import Safe from './components/Safe';
import Boot, { SplashGate } from './components/Boot';
import { colors, fonts } from './constants/theme';

const RootStack = createNativeStackNavigator<RootStackParamList>();

// Safe lazy screen loader. A screen module that runs native side-effects at
// module-eval (the LiveKit/WebRTC screens: top-level registerGlobals + a
// requireNativeComponent import) can THROW while Hermes evaluates it in a release
// archive — leaving `require(...)` === undefined, so `require(...).default` throws
// "Cannot read property 'default' of undefined" and crashes navigation. This
// wraps the require so any eval failure degrades to a graceful fallback screen
// instead of crashing the app. (<Safe> can't help here — this is module-eval,
// not React render.)
function safeLazyScreen(loader: () => any, title: string, message: string) {
  return () => {
    try {
      const mod = loader();
      const C = mod && (mod.default || mod);
      if (typeof C === 'function' || (C && (C as any).$$typeof)) return C;
    } catch (e) {
      console.warn(`[CRYB] screen "${title}" failed to load:`, e);
    }
    // NEUTRAL fallback (NOT the crypto "Coming Soon — CRYB v2" screen): the voice/
    // video screens are shipping SOCIAL features, so a module load failure must
    // read as a transient "couldn't start here", never as "coming in v2" (#69/#71).
    return makeUnavailable(title, message);
  };
}

const navTheme = {
  ...DefaultTheme,
  colors: { ...DefaultTheme.colors, background: colors.bg, primary: colors.primary },
};

const headerStyle = {
  headerShown: true,
  headerTitleStyle: { ...fonts.semibold, fontSize: 17, color: colors.ink },
  headerTintColor: colors.primary,
  headerStyle: { backgroundColor: colors.bg },
  headerShadowVisible: false,
  // Triage P1 (IMG_5939): the back button leaked the previous ROUTE NAME
  // ("Tabs") as its label — show the chevron only.
  headerBackButtonDisplayMode: 'minimal',
} as const;

function AppNavigator({ state }: { state: ReturnType<typeof useAuth>['state'] }) {
  // [GATE] (blueprint §2): unauthed → AuthStack; onboarding → Onboarding only;
  // authed → the full app. Loading renders nothing (splash covers it in P2).
  if (state === 'loading') return null;

  return (
    <RootStack.Navigator screenOptions={{ headerShown: false }}>
      {state === 'unauthed' ? (
        <RootStack.Screen name="Auth">
          {() => <AuthStack initialRouteName="Login" />}
        </RootStack.Screen>
      ) : state === 'onboarding' ? (
        // Onboarding-exempt branch: a DISTINCT screen name (not "Auth") so React
        // Navigation actually REMOUNTS the stack when the gate flips
        // unauthed→onboarding. With the same "Auth" name the screen set is
        // unchanged, the stack is NOT remounted, and initialRouteName="Onboarding"
        // (which only applies on first mount) is ignored — leaving a freshly
        // signed-up user stranded on the Register screen ("nothing happens").
        <RootStack.Screen name="AuthOnboarding">
          {() => <AuthStack initialRouteName="Onboarding" />}
        </RootStack.Screen>
      ) : (
        <>
          {/* Tabs + the flat root-stack screens (blueprint §2) */}
          <RootStack.Screen name="Tabs" component={Tabs} />
          <RootStack.Screen name="Settings" component={SettingsStack} />
          <RootStack.Screen name="Crypto" component={CryptoStack} />

          <RootStack.Screen name="Post" component={PostDetailScreen} options={{ ...headerStyle, title: 'Post' }} />
          <RootStack.Screen name="Community" component={CommunityScreen} options={{ ...headerStyle, title: 'Community' }} />
          <RootStack.Screen name="UserProfile" component={UserProfileScreen} options={{ ...headerStyle, title: 'Profile' }} />
          <RootStack.Screen name="Channel" component={ChannelScreen} options={{ ...headerStyle, title: 'Channel' }} />
          <RootStack.Screen name="ServerDetail" component={ServerDetailScreen} options={{ ...headerStyle, title: 'Server' }} />
          <RootStack.Screen name="Legal" component={LegalScreen} options={{ ...headerStyle, title: 'Legal' }} />
          <RootStack.Screen name="ServersDiscover" component={DiscoverServersScreen} options={{ ...headerStyle, title: 'Discover' }} />
          <RootStack.Screen name="DM" component={DMScreen} options={{ ...headerStyle, title: 'Message' }} />
          <RootStack.Screen name="DMNew" component={DMNewScreen} options={{ ...headerStyle, title: 'New Message', presentation: 'modal' }} />
          {/* NotificationsScreen renders its OWN in-screen glass header (back +
              title + Mark all read), so hide the native one to avoid a duplicate. */}
          <RootStack.Screen name="Notifications" component={NotificationsScreen} options={{ headerShown: false }} />
          <RootStack.Screen name="Search" component={SearchScreen} options={{ ...headerStyle, title: 'Explore' }} />
          <RootStack.Screen name="EditProfile" component={EditProfileScreen} options={{ ...headerStyle, title: 'Edit Profile' }} />
          <RootStack.Screen name="LeaderApply" component={LeaderApplyScreen} options={{ headerShown: false }} />
          <RootStack.Screen name="LeaderDashboard" component={LeaderDashboardScreen} options={{ headerShown: false }} />
          <RootStack.Screen name="LeaderOnboarding" component={LeaderOnboardingScreen} options={{ headerShown: false, presentation: 'fullScreenModal' }} />
          <RootStack.Screen name="CommunityManage" component={CommunityManageScreen} options={{ headerShown: false }} />
          <RootStack.Screen name="Wallet" component={makeComingSoon('Wallet', 'Send, receive & track your crypto — natively in CRYB.')} options={{ ...headerStyle, title: 'Wallet' }} />
          <RootStack.Screen name="Bookmarks" component={BookmarksScreen} options={{ ...headerStyle, title: 'Bookmarks' }} />
          <RootStack.Screen name="Communities" component={CommunitiesScreen} options={{ ...headerStyle, title: 'Communities' }} />
          <RootStack.Screen name="CreatePost" component={CreatePostScreen} options={{ ...headerStyle, title: 'New Post', presentation: 'modal' }} />
          <RootStack.Screen name="CreateCommunity" component={CreateCommunityScreen} options={{ ...headerStyle, title: 'Create Community', presentation: 'modal' }} />
          <RootStack.Screen name="FollowList" component={FollowListScreen} options={{ ...headerStyle, title: 'Connections' }} />
          <RootStack.Screen name="LearnIndex" component={LearnScreen} options={{ ...headerStyle, title: 'Learn' }} />
          <RootStack.Screen name="LearnArticle" component={LearnArticleScreen} options={{ ...headerStyle, title: 'Article' }} />
          <RootStack.Screen name="NftMarketplace" component={makeComingSoon('NFT Gallery', 'Discover, collect & flex NFTs without leaving CRYB.')} options={{ ...headerStyle, title: 'NFT Gallery' }} />
          <RootStack.Screen name="NftMint" component={makeComingSoon('Mint NFT', 'Mint your own NFTs in a few taps.')} options={{ ...headerStyle, title: 'Mint' }} />
          <RootStack.Screen name="NftDetail" component={makeComingSoon('NFT', 'NFT details, offers & provenance — landing in v2.')} options={{ ...headerStyle, title: 'NFT' }} />
          <RootStack.Screen name="MarketsHub" component={MarketsScreen} options={{ ...headerStyle, title: 'Markets' }} />
          <RootStack.Screen name="CoinDetail" component={CoinDetailScreen} options={{ ...headerStyle, title: 'Coin' }} />
          <RootStack.Screen name="Portfolio" component={PortfolioScreen} options={{ ...headerStyle, title: 'Your Portfolio' }} />
          <RootStack.Screen name="VoiceRooms" component={VoiceRoomsScreen} options={{ ...headerStyle, title: 'Voice Rooms' }} />
          <RootStack.Screen name="VoiceRoom" getComponent={safeLazyScreen(() => require('./screens/VoiceRoomScreen'), 'Live room unavailable', "This live room couldn't start on your device. Please try again shortly.")} options={{ ...headerStyle, title: 'Voice Room' }} />
          <RootStack.Screen name="VoiceCall" getComponent={safeLazyScreen(() => require('./screens/VoiceCallScreen'), 'Call unavailable', "This call couldn't start on your device. Please try again shortly.")} options={{ headerShown: false, presentation: 'fullScreenModal' }} />
          <RootStack.Screen name="StoryCreate" component={StoryCreateScreen} options={{ ...headerStyle, title: 'Create Story', presentation: 'fullScreenModal' }} />
          <RootStack.Screen name="StoryViewer" component={StoryViewerScreen} options={{ presentation: 'fullScreenModal' }} />
          <RootStack.Screen name="Events" component={makeComingSoon('Events', 'Community events & meetups are landing soon.')} options={{ ...headerStyle, title: 'Events' }} />
        </>
      )}
    </RootStack.Navigator>
  );
}

export default function App() {
  // The auth gate is resolved ONCE here and shared with the navigator AND the
  // cross-cutting side-effect hosts below. <Boot /> mounts only while authed so
  // its boot effects (socket connect, block-registry sync, social graph, push,
  // foreground reconcile) run with a valid session and tear down on logout.
  const { state } = useAuth();
  const isAuthed = state === 'authed';

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      {/* Root render-crash net (triage P0: blank white screen on any render
          throw in release). Catches + shows a branded recovery screen. */}
      <ErrorBoundary>
      <SafeAreaProvider>
        <StatusBar barStyle="dark-content" backgroundColor={colors.bg} />
        {/* P0-B SPLASH GATE — single controlled render path (no funky flash).
            The app tree ALWAYS renders (mounts warm) behind a branded JS splash
            overlay that covers the native-storyboard → JS handoff, then fades out
            once the auth gate leaves 'loading' (ready) and the splash entrance
            settled. No intermediate `null`/unstyled frame, no tree swap. */}
        {/* CRASH GUARD (RN 0.85 bridgeless New Arch): reanimated ENTERING/EXITING
            layout animations abort at mount on the UI worklet runtime
            (WorkletRuntime::runSync → scheduleOnUI → SIGABRT), which took down the
            app ~0.7s after the authed tree mounted. skipEntering/skipExiting
            neutralises the whole class app-wide so no layout animation can ever
            crash a screen at mount. Imperative useAnimatedStyle/withTiming/gestures
            are unaffected. */}
        <LayoutAnimationConfig skipEntering skipExiting>
        <SplashGate ready={state !== 'loading'}>
          <NavigationContainer ref={navigationRef} linking={linking} theme={navTheme}>
            <AppNavigator state={state} />
            {/* Authenticated-session boot orchestration (ported from the Expo
                _layout.tsx boot effects). Mounted above the navigator (uses
                navigationRef, not a screen hook) and ONLY while authed, so every
                effect's cleanup fires on logout. Renders nothing. */}
            {isAuthed && <Boot />}
            {/* LR7: foreground in-app notification banner + app-icon badge sync.
                Mounted above the navigator (uses navigationRef, not a screen hook).
                Self-guards: renders null until a socket notification arrives. */}
            {/* Root overlays each isolated in <Safe>: a render throw in any one
                degrades to nothing instead of white-screening the whole app. */}
            <Safe label="InAppNotification"><InAppNotification /></Safe>
            <Safe label="IncomingCallHandler"><IncomingCallHandler /></Safe>
            {/* P2-DRAWER: X-style account drawer overlay (renders null until opened). */}
            <Safe label="DrawerMenu"><DrawerMenu /></Safe>
            {/* App-wide "call in progress — tap to return" banner (WhatsApp-style). */}
            <Safe label="CallInProgressBanner"><CallInProgressBanner /></Safe>
          </NavigationContainer>
        </SplashGate>
        </LayoutAnimationConfig>
      </SafeAreaProvider>
      </ErrorBoundary>
    </GestureHandlerRootView>
  );
}
